import { CampaignApplicationResolutionStatus, CampaignApplicationState, Gender } from "src/graphql/generated/graphql";
import { CampaignApplicationRestApi, ReviewerRestApi } from "src/types";
import { handleError } from "src/utils/error";

import * as protocol from "./protocol";

const parseCampaignApplicationUpdateKey = (campaignApplication: CampaignApplicationRestApi): string => {
    return `${campaignApplication?.id}${campaignApplication?.state}${campaignApplication?.pendingResolution?.status}${campaignApplication?.latestResolution?.status}`;
};

const deserializeGender = (gender: protocol.ReviewerGender): Gender => {
    switch (gender) {
        case protocol.ReviewerGender.male:
            return Gender.Male;
        case protocol.ReviewerGender.female:
            return Gender.Female;
    }
};

const deserializeCampaignApplicationState = (state: protocol.CampaignApplicationState): CampaignApplicationState => {
    switch (state) {
        case protocol.CampaignApplicationState.draft:
            return CampaignApplicationState.Draft;
        case protocol.CampaignApplicationState.submitted:
            return CampaignApplicationState.Submitted;
        case protocol.CampaignApplicationState.pending:
            return CampaignApplicationState.Pending;
        case protocol.CampaignApplicationState.resolved:
            return CampaignApplicationState.Resolved;
    }

    return null;
};

const deserializeCampaignApplicationResolutionStatus = (
    status: protocol.CampaignApplicationResolutionStatus
): CampaignApplicationResolutionStatus => {
    switch (status) {
        case protocol.CampaignApplicationResolutionStatus.selected:
            return CampaignApplicationResolutionStatus.Selected;
        case protocol.CampaignApplicationResolutionStatus.alternate:
            return CampaignApplicationResolutionStatus.Alternate;
        case protocol.CampaignApplicationResolutionStatus.rejected:
            return CampaignApplicationResolutionStatus.Rejected;
    }

    return null;
};

const deserializeReviewer = (reviewer: protocol.IReviewer): ReviewerRestApi => {
    return {
        id: reviewer?.id,
        profile: {
            firstName: reviewer?.profile?.firstName,
            lastName: reviewer?.profile?.lastName,
            gender: deserializeGender(reviewer?.profile?.gender),
            birthDate: reviewer?.profile?.birthDate ? Number(reviewer.profile.birthDate) : null,
            age: typeof reviewer?.profile?.age === "number" ? Number(reviewer.profile.age) : null,
            phoneNumber: reviewer?.profile?.phoneNumber,
            address: {
                country: reviewer?.profile?.address?.country,
                postalCode: reviewer?.profile?.address?.postalCode,
                city: reviewer?.profile?.address?.city,
                line1: reviewer?.profile?.address?.line1,
                line2: reviewer?.profile?.address?.line2,
            },
        },
        profileImage: reviewer?.profileImage?.src
            ? {
                  id: null,
                  objectKey: null,
                  src: reviewer?.profileImage?.src,
              }
            : null,
        socials: {
            hasFb: reviewer?.socials?.hasFb,
            fbFollowers: reviewer?.socials?.fbFollowers,
            hasIg: reviewer?.socials?.hasIg,
            igFollowers: reviewer?.socials?.igFollowers,
        },
        isBlacklisted: reviewer?.isBlacklisted,
        blacklistedUntil: reviewer?.blacklistedUntil ? Number(reviewer.blacklistedUntil) : null,
        totalApplicationsCount: reviewer?.totalApplicationsCount,
        selectedApplicationsCount: reviewer?.selectedApplicationsCount,
        alternateApplicationsCount: reviewer?.alternateApplicationsCount,
        rejectedApplicationsCount: reviewer?.rejectedApplicationsCount,
        clientCounts: {
            any: reviewer.clientCounts?.any || 0,
            selected: reviewer.clientCounts?.selected || 0,
            alternate: reviewer.clientCounts?.alternate || 0,
            rejected: reviewer.clientCounts?.rejected || 0,
        },
    };
};

const deserializeCampaignApplication = (
    campaignApplication: protocol.ICampaignApplication
): CampaignApplicationRestApi => {
    return {
        id: campaignApplication?.id,
        campaign: null,
        reviewer: deserializeReviewer(campaignApplication?.reviewer),
        state: deserializeCampaignApplicationState(campaignApplication?.state),
        appliedAt: campaignApplication?.appliedAt ? Number(campaignApplication.appliedAt) : null,
        isUnselected: campaignApplication?.isUnselected,
        questionnaireResponse: {
            questionnaire: null,
            answers: (campaignApplication?.questionnaireResponseAnswers || []).map((answer) => {
                let value = null;

                try {
                    value = JSON.parse(answer?.value);
                } catch (err) {
                    handleError(err, {
                        context: { campaignApplicationId: campaignApplication?.id, answer: answer?.value },
                    });
                }

                return {
                    key: answer?.key,
                    value,
                };
            }),
        },
        deliveryAddress: {
            country: campaignApplication?.deliveryAddress?.country,
            postalCode: campaignApplication?.deliveryAddress?.postalCode,
            city: campaignApplication?.deliveryAddress?.city,
            line1: campaignApplication?.deliveryAddress?.line1,
            line2: campaignApplication?.deliveryAddress?.line2,
        },
        pendingResolution: {
            status: deserializeCampaignApplicationResolutionStatus(campaignApplication?.pendingResolution?.status),
            resolvedAt: campaignApplication?.pendingResolution?.resolvedAt
                ? Number(campaignApplication.pendingResolution.resolvedAt)
                : null,
        },
        latestResolution: {
            status: deserializeCampaignApplicationResolutionStatus(campaignApplication?.latestResolution?.status),
            resolvedAt: campaignApplication?.latestResolution?.resolvedAt
                ? Number(campaignApplication.latestResolution.resolvedAt)
                : null,
        },
        resolutions: (campaignApplication?.resolutions || []).map((resolution) => ({
            status: deserializeCampaignApplicationResolutionStatus(resolution?.status),
            resolvedAt: resolution?.resolvedAt ? Number(resolution.resolvedAt) : null,
        })),
        discountCode: campaignApplication?.discountCode,
        isOrdered: campaignApplication?.isOrdered,
        orderConfirmedAt: campaignApplication?.orderConfirmedAt ? Number(campaignApplication.orderConfirmedAt) : null,
        isDelivered: campaignApplication?.isDelivered,
        deliveryConfirmedAt: campaignApplication?.deliveryConfirmedAt
            ? Number(campaignApplication.deliveryConfirmedAt)
            : null,
        dueReviewNotificationsAt: (campaignApplication?.dueReviewNotificationsAt || []).map((notificationAt) =>
            Number(notificationAt)
        ),
        deadlineAt: campaignApplication?.deadlineAt ? Number(campaignApplication.deadlineAt) : null,
        submittedReviewsCount: campaignApplication?.submittedReviewsCount,
    };
};

export const deserializeGetCampaignApplicationsResponse = async (
    data: string
): Promise<{
    campaignApplications: CampaignApplicationRestApi[];
    updateKey: string;
    nextToken?: string;
    requestId?: string;
}> => {
    const dataResponse = await fetch(`data:application/octet-stream;base64,${data}`);
    const requestId = dataResponse.headers.get("x-amzn-requestid");
    const dataBuffer = new Uint8Array(await dataResponse.arrayBuffer());

    const response = await protocol.GetCampaignApplicationsResponse.decode(dataBuffer);

    let updateKey = "";

    const campaignApplications = (response?.items || []).map((item) => {
        const campaignApplication = deserializeCampaignApplication(item);
        updateKey = `${updateKey}${parseCampaignApplicationUpdateKey(campaignApplication)}`;
        return campaignApplication;
    });

    return {
        campaignApplications,
        updateKey,
        nextToken: response?.nextToken,
        requestId,
    };
};
